import $, { Cash } from 'cash-dom';
import { publish } from '../humans/helper';

/**
 * Sorting Module
 * Handles sorting of index list items based on data attributes
 * @package KAA
 */
export default class Sorting {
    /**
     * Singleton instance of the component
     */
    static #instance: Sorting;

    /**
     * Cash body reference
     */
    protected $body: Cash;

    /**
     * UI references
     */
    protected ui = {
        sortingButtons: '.js-sorting-default, .js-sorting-alternative',
        sortingItem: '.js-sorting-item',
        roleFilterCheckboxes: '.js-pseudo-filter input[data-filter-type="role"]',
    };

    /**
     * Sorting constructor
     */
    protected constructor() {
        this.$body = $('body');
        this.registerEvents();
    }

    /**
     * Returns the singleton instance of the component
     */
    public static getInstance(): Sorting {
        if (!Sorting.#instance) {
            Sorting.#instance = new Sorting();
        }

        return Sorting.#instance;
    }

    /**
     * Register required DOM events
     */
    protected registerEvents(): void {
        // Manual sorting buttons (Oldest/Newest)
        this.$body.on('click', this.ui.sortingButtons, this.onSortingButtonClick.bind(this));

        // Automatic sorting for role filters (leadership, partners, associates)
        if ($(this.ui.roleFilterCheckboxes).length > 0) {
            this.$body.on('change', this.ui.roleFilterCheckboxes, this.onRoleFilterChange.bind(this));
        }
    }

    /**
     * Handle any sorting button click
     */
    protected onSortingButtonClick(event: Event): void {
        const $button = $(event.currentTarget as HTMLElement);

        // Do nothing if already active
        if ($button.hasClass('is-active')) return;

        // Get section from parent filter component
        const $filterForm = $button.closest('.js-pseudo-filter');
        const section = $filterForm.data('section') as string;

        // Find container
        const $firstItem = $(this.ui.sortingItem).first();
        if (!$firstItem.length) return;
        const $container = $firstItem.parent();
        if (!$container.length) return;

        // Determine sort type and sort
        const useDefault = $button.hasClass('js-sorting-default');
        this.sortItems($container, useDefault);

        // Update button states
        $(this.ui.sortingButtons)
            .removeClass('is-active')
            .attr('aria-pressed', 'false');

        $button
            .addClass('is-active')
            .attr('aria-pressed', 'true');

        // Scroll to sorted section
        if (section) {
            setTimeout(() => {
                this.scrollToList(section);
            }, 50);
        }
    }

    /**
     * Sort items in the container based on sorting type
     */
    protected sortItems($container: Cash, useDefault: boolean): void {
        const $items = $container.find(this.ui.sortingItem);
        if (!$items.length) return;

        // Convert to array and sort
        const itemsArray: HTMLElement[] = [];
        $items.each((_, item) => itemsArray.push(item));

        const sortAttribute = useDefault ? 'data-sorting-default' : 'data-sorting-alternative';
        itemsArray.sort((a, b) => {
            const aValue = parseInt($(a).attr(sortAttribute) || '0', 10);
            const bValue = parseInt($(b).attr(sortAttribute) || '0', 10);
            return aValue - bValue;
        });

        // Reorder in DOM
        itemsArray.forEach(item => $container.append(item));
    }

    /**
     * Handle role filter changes and auto-sort
     * Applies to leadership, partners, associates filters
     */
    protected onRoleFilterChange(): void {
        // Check if any role filter is active
        const $roleCheckboxes = $(this.ui.roleFilterCheckboxes + ':checked');
        const isRoleFilterActive = $roleCheckboxes.length > 0;

        // Find the container
        const $firstItem = $(this.ui.sortingItem).first();
        if (!$firstItem.length) return;
        const $container = $firstItem.parent();
        if (!$container.length) return;

        // Add / remove filtered-by-role class
        if (isRoleFilterActive) {
            $container.addClass('filtered-by-role');
        } else $container.removeClass('filtered-by-role');

        // Sort based on role filter (role active = use alternative sorting)
        this.sortItems($container, !isRoleFilterActive);
    }

    /**
     * Scroll to list top without triggering view transition
     * Calculates the correct offset to align with sticky headers
     */
    protected scrollToList(sectionId: string): void {
        const $targetSection = $(`#${sectionId}`);

        if (!$targetSection.length) return;

        // Publish anchor scrolling
        publish('anchorScroll.start', true);

        // Calculate accurate offset
        let offsetTop = $targetSection.offset()?.top ?? 0;

        // Create a temporary element to resolve the correct top scroll
        const tempElement = document.createElement("div");
        tempElement.classList.add('register-top-helper');
        $targetSection[0]?.appendChild(tempElement);

        // Get computed height in pixels
        const registerListTopPx = parseFloat(getComputedStyle(tempElement).height);

        // Remove the temporary element after calculation
        tempElement.remove();

        // Add the custom property value to offsetTop
        offsetTop -= registerListTopPx;

        // Instant scroll without view transition
        window.scrollTo({
            top: offsetTop,
            behavior: "instant"
        });

        // Remove anchor scrolling status
        window.setTimeout(() => {
            publish('anchorScroll.end', true);
        }, 50);
    }

    /**
     * Check for breakpoint
     */
    protected isMobile(): boolean {
        return window.innerWidth < 1024;
    }
}